import { Component, computed, forwardRef, inject, input, model } from '@angular/core';
import { DisplayMedia } from 'src/app/models/image';
import { IonIcon } from "@ionic/angular/standalone";
import { FileService } from 'src/app/services/file.service';
import { ToastService } from 'src/app/services/toast.service';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PdfViewerComponent } from "../../pdf-viewer/pdf-viewer.component";
import { LocalizationModule } from "../../../../modules/localization/localization.module";

@Component({
  selector: 'app-pdf-picker',
  templateUrl: './pdf-picker.component.html',
  styleUrls: ['./pdf-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PdfPickerComponent),
      multi: true,
    }
  ],
  standalone: true,
  imports: [IonIcon, PdfViewerComponent, LocalizationModule],
})
export class PdfPickerComponent implements ControlValueAccessor {
  fileService = inject(FileService);
  toastService = inject(ToastService);

  value = model<DisplayMedia | null>(null);
  maxFileSizeMb = model<number>(5);
  errorMessage = input<string>();
  htmlFor = input.required<string>();
  disabled = model<boolean>();

  supportedMimeTypes = computed(() => {
    const mediaType = 'application';
    const supportedFileTypes = this.fileService.supportedFileFormats()[mediaType].map(t => t.mimeSubType);
    let unique = Array.from(new Set(supportedFileTypes));
    return unique.map(t => `${mediaType}/${t}`);
  });
  supportedMimeTypesString = computed<string>(() => {
    return this.supportedMimeTypes().join(',');
  });

  onChange = (value: DisplayMedia | null) => { };
  onTouched = () => { };

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value: DisplayMedia): void {
    this.value.set(value);
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  addMedia(event: Event) {
    const el = event.target as HTMLInputElement;
    const file = el.files?.[0];
    el.value = '';
    if (!file) return;
    const supported = this.supportedMimeTypes().some(s => s === file.type);
    if (!supported) {
      this.toastService.error(`File type ${file.type} not supported`);
      return;
    }
    const objectName = this.fileService.generateObjectName({ file, bucketName: 'pdf-custom-field' });
    const mediaToAdd: DisplayMedia = {
      status: 'new',
      bucketName: 'pdf-custom-field',
      objectName,
      originalFile: file,
    };
    this.value.set(mediaToAdd);
    this.onChange(mediaToAdd);
    this.onTouched();
  }

  remove() {
    if (this.disabled()) return;
    const value = this.value();
    if (value?.fileReferenceId) {
      value.status = 'deleted';
      if (value.src) {
        URL.revokeObjectURL(value.src);
      }
    }
    this.value.set(null);
    this.onChange(null);
    this.onTouched();
  }
}
